import Toast from 'react-native-toast-message';
import { supabase } from './client';
import { getCurrentOrgID } from './organization';
import ErrorHandler from '@/utils/ErrorHandler';
import { isTransientError } from '@/utils/requestQueue';
import { extractAutomationCode } from '@/utils/automationBuilder';

/**
 * Canonical client contract for schemaVersion-2 Automations (spec §16.4:
 * src/data/automations.js). Thin authenticated adapters over the
 * automation_* RPC surface; legacy (schemaVersion 1) definitions keep
 * using src/data/paths.js — this module never writes v1 state.
 */

const AUTOMATION_ERROR_MESSAGES = {
    AUTOMATION_DENIED: "You don't have permission to do that. Contact your Reach administrator if you think you should.",
    AUTOMATION_NOT_FOUND: "That automation couldn't be found. It may have been removed.",
    AUTOMATION_INVALID: 'This automation definition is invalid. Fix the highlighted problems and try again.',
    AUTOMATION_CONFLICT: 'This automation changed since you opened it. Reload and publish again.',
    AUTOMATION_INACTIVE: 'This automation is disabled.',
    AUTOMATION_PERSON_REQUIRED: 'This automation requires a person to run.',
};

/** Error carrying the stable AUTOMATION_* contract code for UI handling. */
export class AutomationError extends Error {
    constructor(code, fallbackMessage, original) {
        super(AUTOMATION_ERROR_MESSAGES[code] ?? fallbackMessage ?? original?.message ?? 'Something went wrong');
        this.name = 'AutomationError';
        this.automationCode = code ?? null;
        this.originalError = original ?? null;
        // Contract errors are expected — never report them to Sentry.
        this.expected = !!code;
    }
}

const handleAutomationRpcError = (error, { operation, params = {}, toast = false, friendly } = {}) => {
    const code = extractAutomationCode(error);
    if (code) {
        const automationError = new AutomationError(code, friendly, error);
        if (toast) Toast.show({ type: 'error', text2: automationError.message });
        return automationError;
    }
    const wrapped = Error(error.message || 'Automation request failed');
    wrapped.code = error.code;
    wrapped.status = error.status;
    wrapped.originalError = error;
    if (isTransientError(error)) return wrapped;
    ErrorHandler(wrapped, { query: operation, ...params, error }, friendly ?? 'Something went wrong with that automation');
    return wrapped;
};

const callAutomationRpc = async (name, params, options = {}) => {
    const { data, error } = await supabase.rpc(name, params);
    if (error) {
        throw handleAutomationRpcError(error, {
            operation: name,
            params: options.params ?? params,
            toast: options.toast,
            friendly: options.friendly,
        });
    }
    return data;
};

/**
 * The global trigger registry (one row per triggerType: family, sourceParam,
 * personOptional, inputNamespace, inputSpec). Read-only, not org-scoped.
 */
export const getAutomationTriggerCatalog = async () => {
    const { data, error } = await supabase
        .from('AutomationTriggerCatalog')
        .select('triggerType, family, sourceType, sourceParam, personOptional, inputNamespace, inputSpec, isActive')
        .eq('isActive', true)
        .order('triggerType', { ascending: true });
    if (error) {
        ErrorHandler(Error(error.message), { query: 'getAutomationTriggerCatalog', error }, 'Something went wrong loading triggers');
        return [];
    }
    return data || [];
};

/**
 * automation_get_variables(pathID) — the source-aware variable registry
 * (§14.5). Returns {variables, steps, triggerType, personOptional,
 * schemaVersion, compatibilityMode}.
 */
export const fetchAutomationVariables = (pathID) =>
    callAutomationRpc('automation_get_variables', { p_path_id: pathID },
        { params: { pathID } });

/**
 * automation_validate_bindings(pathID, definition?) — static validation for
 * impact preview before publish. Returns {valid, errors, warnings}.
 */
export const validateAutomationBindings = ({ pathID, definition = {} }) =>
    callAutomationRpc('automation_validate_bindings', {
        p_path_id: pathID,
        p_definition: definition,
    }, { params: { pathID } });

/**
 * automation_publish(pathID, payload) — atomic publish: expectedRevision
 * concurrency check, full definition validation, revision bump +
 * PathDefinitionRevisions audit row, and the live reconcile sweep. Returns
 * {revision, schemaVersion, compatibilityMode, activeRuns, staleQueuedSteps,
 * impact, reconcile, warnings}. Throws AutomationError with
 * automationCode 'AUTOMATION_CONFLICT' on a stale expectedRevision.
 */
export const publishAutomation = ({ pathID, payload }) =>
    callAutomationRpc('automation_publish', {
        p_path_id: pathID,
        p_payload: payload,
    }, { params: { pathID, expectedRevision: payload?.expectedRevision ?? null } });

/**
 * automation_start_run(pathID, input, idempotencyKey?) — person-optional run
 * start for v2 definitions. Returns {runID, pathID, schemaVersion, deduped,
 * capturedInput}.
 */
export const startAutomationRun = ({ pathID, input = {}, idempotencyKey = null }) =>
    callAutomationRpc('automation_start_run', {
        p_path_id: pathID,
        p_input: input,
        p_idempotency_key: idempotencyKey,
    }, { params: { pathID }, toast: true });

/**
 * Resume a parked review step on a v2 run (§15.4). Mirrors the publish
 * sweep's REVISION_REVIEW disposition, applied to ONE step on demand: the
 * park is transient pending review, so resume stamps waitState RESUMED,
 * flips the step back to PENDING, and re-enqueues it — the worker then
 * executes the CURRENT published config (a stale queue payload is never
 * replayed). Only applies to steps still parked for review; a step whose
 * PathStep was removed has no resume path (the run detail offers
 * "continue past" via manually_advance_step instead).
 * PROVIDER_UNCERTAIN retries keep using retry_path_step (FAILED → PENDING).
 */
export const resumeParkedAutomationStep = async ({ enrollmentStepID }) => {
    const now = new Date().toISOString();
    const { data: step, error: readError } = await supabase
        .from('PathEnrollmentSteps')
        .select('id, status, waitState')
        .eq('id', enrollmentStepID)
        .maybeSingle();

    if (readError) {
        ErrorHandler(Error(readError.message), { mutation: 'resumeParkedAutomationStep', enrollmentStepID, error: readError }, 'Something went wrong resuming that step');
        throw Error();
    }
    const kind = step?.waitState?.kind;
    const resumable = step?.status === 'WAITING_MANUAL'
        && step?.waitState?.outcome === 'REQUIRES_REVIEW'
        && (kind === 'REVISION_REVIEW' || kind === 'STEP_REVIEW');
    if (!resumable) {
        Toast.show({ type: 'error', text2: 'That step is no longer waiting for review.' });
        return { resumed: false };
    }

    const { error: updateError } = await supabase
        .from('PathEnrollmentSteps')
        .update({
            status: 'PENDING',
            queueMsgId: null,
            waitState: {
                ...step.waitState,
                outcome: 'RESUMED',
                resumedAt: now,
                resumedBy: 'MANUAL_REVIEW',
            },
        })
        .eq('id', enrollmentStepID)
        .eq('status', 'WAITING_MANUAL');

    if (updateError) {
        ErrorHandler(Error(updateError.message), { mutation: 'resumeParkedAutomationStep', enrollmentStepID, error: updateError }, 'Something went wrong resuming that step');
        throw Error();
    }

    const { error: enqueueError } = await supabase.rpc('enqueue_path_step', {
        p_enrollment_step_id: enrollmentStepID,
    });
    if (enqueueError) {
        ErrorHandler(Error(enqueueError.message), { mutation: 'resumeParkedAutomationStep', enrollmentStepID, error: enqueueError }, 'Something went wrong resuming that step');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Step resumed on the latest version.' });
    return { resumed: true };
};

/**
 * automation_record_approval(taskInstanceID, decision, note?) — an approver's
 * decision on a REQUEST_APPROVAL step's Individual task. The server stamps
 * TaskInstances.approvalOutcome/approvalDecidedAt/approvalDecidedByPersonID
 * and resolves the parked step's outcome. Returns {decision} on success or
 * {already:true, outcome} when another approver already decided.
 */
export const recordAutomationApproval = ({ taskInstanceID, decision, note = null }) =>
    callAutomationRpc('automation_record_approval', {
        p_task_instance_id: taskInstanceID,
        p_decision: decision,
        p_note: note || null,
    }, { params: { taskInstanceID, decision } });

/**
 * Create a schemaVersion-2 GENERIC automation: the Paths row plus its one
 * TRIGGER step (config.triggerType + config.triggerConfig) — the same shape
 * automation_validate_bindings reads. Created inactive so a half-configured
 * trigger can never fire; the builder activates it via publish.
 */
export const createAutomation = async ({ name, description, createdBy, triggerType, triggerConfig = {}, inputSchema = null }) => {
    const orgID = await getCurrentOrgID();

    const { data: path, error } = await supabase
        .from('Paths')
        .insert({
            organizationID: orgID,
            name,
            description: description || null,
            isActive: false,
            schemaVersion: 2,
            compatibilityMode: 'GENERIC',
            inputSchema: inputSchema || {},
            createdBy: createdBy || null,
            updatedAt: new Date().toISOString(),
        })
        .select('id, name, schemaVersion, compatibilityMode, revision')
        .maybeSingle();

    if (error || !path) {
        ErrorHandler(Error(error?.message || 'create failed'), { mutation: 'createAutomation', error }, 'Something went wrong creating that automation');
        throw Error();
    }

    const { error: stepError } = await supabase
        .from('PathSteps')
        .insert({
            organizationID: orgID,
            pathID: path.id,
            position: 0,
            name: 'Trigger',
            stepType: 'TRIGGER',
            config: { triggerType, triggerConfig },
            advanceOn: 'AUTOMATIC',
        });

    if (stepError) {
        ErrorHandler(Error(stepError.message), { mutation: 'createAutomation.trigger', pathID: path.id, error: stepError }, 'Something went wrong creating the trigger');
        // Roll the definition back so we never leave a trigger-less v2 row.
        await supabase.from('Paths').delete().eq('id', path.id);
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Automation created! Configure its steps, then publish.' });
    return path;
};

/**
 * Save the trigger step's config for a v2 definition (triggerType +
 * triggerConfig). Uses the same PathSteps row as v1; the server enforces
 * one-trigger and binding compatibility at publish.
 */
export const saveAutomationTrigger = async ({ stepID, pathID, position, name, triggerType, triggerConfig }) => {
    const orgID = await getCurrentOrgID();
    const payload = {
        organizationID: orgID,
        pathID,
        position: position ?? 0,
        name: name || 'Trigger',
        stepType: 'TRIGGER',
        actionType: null,
        config: { triggerType, triggerConfig: triggerConfig || {} },
        isEnabled: true,
        advanceOn: 'AUTOMATIC',
    };
    let result;
    if (stepID) {
        const { error, data } = await supabase.from('PathSteps').update(payload).eq('id', stepID).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'saveAutomationTrigger', stepID, error }, 'Something went wrong saving that trigger');
            throw Error();
        }
        result = data;
    } else {
        const { error, data } = await supabase.from('PathSteps').insert(payload).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'saveAutomationTrigger', error }, 'Something went wrong saving that trigger');
            throw Error();
        }
        result = data;
    }
    return result;
};
